var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import DixaIcon from '@dixa/dixa-modern-icons';
import { Badge, Dropdown, Portal, Text, Timestamp, } from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import classnames from 'classnames';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { getHumanReadableExpiration } from 'components/conversation/utils/conversationHumanReadableExpiration';
import { Highlight } from 'components/highlight';
import { buttonize } from 'components/shared/util/buttonize';
import { ConversationStatusIcon } from '../conversationStatusIcon';
import styles from './styles.module.scss';
export class ConversationSummary extends React.PureComponent {
    constructor() {
        super(...arguments);
        this.state = {
            now: Date.now(),
            dropdownOpen: false,
            dropdownOffset: { left: 0, top: 0 },
        };
    }
    getTitle(title, conversationUrl = null, requester) {
        const content = [
            requester && (_jsx(Text.Heading, Object.assign({ as: "span", variant: "heading-3" }, { children: requester }), "requester")),
            requester && _jsx("span", { children: " - " }, "separator"),
            _jsx(Text.Paragraph, Object.assign({ as: "span", variant: "2" }, { children: _jsx(Highlight, { highlightedValue: title }) }), "title"),
        ].filter(Boolean);
        if (!conversationUrl)
            return content;
        return (_jsx(Link, Object.assign({ to: conversationUrl, className: styles.titleLink, onClick: this.props.onClickTitleLink }, { children: content })));
    }
    componentDidMount() {
        this.setNextReplyWindowRefreshTimeout();
    }
    componentDidUpdate(prevProps) {
        const { replyWindowExpiresOn } = this.props;
        if (replyWindowExpiresOn !== prevProps.replyWindowExpiresOn) {
            if (this.updateReplyWindowTimeout) {
                clearTimeout(this.updateReplyWindowTimeout);
            }
            if (replyWindowExpiresOn) {
                this.setNextReplyWindowRefreshTimeout();
            }
        }
    }
    componentWillUnmount() {
        if (this.updateReplyWindowTimeout) {
            clearTimeout(this.updateReplyWindowTimeout);
        }
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
    setNextReplyWindowRefreshTimeout() {
        const { now } = this.state;
        const { replyWindowExpiresOn } = this.props;
        if (!replyWindowExpiresOn) {
            return;
        }
        const smallerTimeout = Math.min(replyWindowExpiresOn.getTime() - now, 5000);
        const timeout = Math.max(smallerTimeout, 0);
        if (timeout > 0) {
            this.updateReplyWindowTimeout = global.setTimeout(this.updateReplyWindow, timeout);
        }
    }
    updateReplyWindow() {
        if (this.updateReplyWindowTimeout) {
            clearTimeout(this.updateReplyWindowTimeout);
        }
        this.setState({ now: Date.now() }, this.setNextReplyWindowRefreshTimeout);
    }
    handleDropdownToggle(event) {
        const { dropdownOpen } = this.state;
        if (!dropdownOpen)
            this.calculateOffset(event.currentTarget);
        this.timeout = global.setTimeout(() => {
            this.setState({
                dropdownOpen: !dropdownOpen,
            });
        }, 0);
    }
    calculateOffset(target) {
        const { left, top } = target.getBoundingClientRect();
        this.setState({
            dropdownOffset: {
                left,
                top,
            },
        });
    }
    closeDropdown() {
        this.setState({
            dropdownOpen: false,
        });
    }
    hasFollowUp(status) {
        return status === 'pending' || status === 'awaitingpending';
    }
    getStatusText() {
        var _a, _b;
        const { labels, conversationInfo } = this.props;
        return labels.queue || ((_a = conversationInfo.queue) === null || _a === void 0 ? void 0 : _a.name) ? (_jsx("strong", { children: labels.queue || ((_b = conversationInfo.queue) === null || _b === void 0 ? void 0 : _b.name) })) : (_jsx("i", { children: "Not in a queue" }));
    }
    render() {
        const _a = this.props, { className, small, light, dropdown, loading, updating, conversationUrl, conversationInfo, conversationAttributes, replyWindowExpiresOn, labels } = _a, otherProps = __rest(_a, ["className", "small", "light", "dropdown", "loading", "updating", "conversationUrl", "conversationInfo", "conversationAttributes", "replyWindowExpiresOn", "labels"]);
        const { dropdownOpen, dropdownOffset } = this.state;
        const { id: UUID, numeric_id: numericCSID = false, status, initial_channel: channel, direction, pending_expiration_at: pendingAt, } = conversationInfo;
        const csid = typeof numericCSID === 'number' ? numericCSID.toString() : UUID;
        const csidString = typeof csid === 'string' || typeof csid === 'number' ? csid : '';
        const { title, requester } = labels;
        const hasFollowUp = status === 'pending' || status === 'awaitingpending';
        const rootClass = classnames({
            [styles.summary]: true,
            [styles.summarySmall]: small,
            [styles.summaryLight]: light,
            [styles.summaryLoading]: !!loading,
            [styles.summaryUpdating]: updating,
        }, className);
        const titleString = title || `#${csidString}`;
        const statusText = this.getStatusText();
        const renderParameters = {
            otherProps,
            rootClass,
            channel,
            direction,
            status,
            loading,
            titleString,
            conversationUrl,
            conversationAttributes,
            requester,
            replyWindowExpiresOn,
            hasFollowUp,
            pendingAt,
            updating,
            dropdown,
            statusText,
            dropdownOffset,
            dropdownOpen,
        };
        return this.renderContent(renderParameters, csidString);
    }
    renderContent(params, csidString) {
        const separator = ' · ';
        const { otherProps, rootClass, channel, direction, status, loading, titleString, conversationUrl, conversationAttributes, requester, replyWindowExpiresOn, hasFollowUp, pendingAt, updating, dropdown, statusText, dropdownOffset, dropdownOpen, } = params;
        return (_jsxs("div", Object.assign({}, otherProps, { className: rootClass }, { children: [direction && (_jsx(ConversationStatusIcon, { channel: channel, direction: direction, status: status, loading: loading, className: styles.iconHolder })), _jsxs("div", Object.assign({ className: styles.textWrap }, { children: [loading && _jsx("div", { className: styles.loadingTitle }), loading && _jsx("div", { className: styles.loadingStatus }), _jsxs("div", Object.assign({ className: styles.title }, { children: [_jsx("span", Object.assign({ className: styles.titleWrap }, { children: this.getTitle(titleString, conversationUrl, requester) })), _jsxs("span", Object.assign({ className: styles.titlePills }, { children: [channel === 'facebookmessenger' &&
                                            (conversationAttributes === null || conversationAttributes === void 0 ? void 0 : conversationAttributes.instagram_page_id) &&
                                            (conversationAttributes === null || conversationAttributes === void 0 ? void 0 : conversationAttributes.instagram_profile_id) && (_jsx(Badge, Object.assign({ variant: "primary", size: "small", className: styles.badge }, { children: "Instagram" }))), replyWindowExpiresOn && (_jsx(Badge, Object.assign({ variant: "warning", size: "small", className: styles.badge }, { children: getHumanReadableExpiration(this.state.now, this.props.replyWindowExpiresOn) }))), hasFollowUp && !!pendingAt && (_jsxs(Badge, Object.assign({ variant: "info", size: "small", className: styles.badge }, { children: ["Expires ", _jsx(Timestamp, { relative: true, time: pendingAt })] }))), updating && (_jsx(Badge, Object.assign({ variant: "secondary", size: "small", className: styles.badge }, { children: "Updating..." })))] }))] }))] })), !dropdown && _jsx("div", Object.assign({ className: styles.status }, { children: statusText })), dropdown && (_jsxs("div", Object.assign({ className: styles.status }, { children: [csidString && (_jsx("span", Object.assign({ className: styles.csidString }, { children: `#${csidString}${separator}` }))), _jsxs("span", Object.assign({}, buttonize(this.handleDropdownToggle), { children: [statusText, _jsx(DixaIcon, { icon: "info-circle", className: classnames('icon-vertical-fix', styles.infoIcon) })] }))] }))), dropdown && (_jsx(Portal, Object.assign({ containerId: "conversation-summary-dropdown-container" }, { children: _jsx("div", Object.assign({ className: styles.dropdownHolder, style: {
                            left: `${dropdownOffset.left}px`,
                            top: `${dropdownOffset.top}px`,
                        } }, { children: _jsx(Dropdown, Object.assign({ className: styles.dropdown, isOpen: dropdownOpen, onClose: this.closeDropdown }, { children: dropdown })) })) })))] })));
    }
}
__decorate([
    boundMethod
], ConversationSummary.prototype, "setNextReplyWindowRefreshTimeout", null);
__decorate([
    boundMethod
], ConversationSummary.prototype, "updateReplyWindow", null);
__decorate([
    boundMethod
], ConversationSummary.prototype, "handleDropdownToggle", null);
__decorate([
    boundMethod
], ConversationSummary.prototype, "closeDropdown", null);
